import React, { useState } from 'react';
import { Row, Col, Input, Button, Space, Typography, Tag } from 'antd';
import { 
    SearchOutlined, 
    ClockCircleOutlined,
    AppstoreOutlined,
    FireOutlined,
    StarOutlined
} from '@ant-design/icons';
import './AuctionSearch.css';

const { Title, Text } = Typography;

const AuctionSearch = ({ 
    searchInput, 
    setSearchInput, 
    filters, 
    setFilters, 
    loading = false,
    auctionCount = 0,
    onSearch,
    onFilterChange,
    showQuickFilters = true,
    title = "Discover Auctions",
    subtitle = "Find the perfect items from our curated collection of premium auctions",
    showAuctionCount = true
}) => {
    const [localSearchInput, setLocalSearchInput] = useState(searchInput || '');
    const [localFilters, setLocalFilters] = useState(filters || { status: 'all', category: 'all' });

    // Update local filters when props change
    React.useEffect(() => {
        setLocalFilters(filters || { status: 'all', category: 'all' });
    }, [filters]);

    // Update local search input when props change
    React.useEffect(() => {
        setLocalSearchInput(searchInput || '');
    }, [searchInput]);

    const handleSearch = (value) => {
        const searchValue = value || localSearchInput;
        setSearchInput(searchValue);
        setLocalSearchInput(searchValue); // Update local state as well
        
        // Apply the local filters when searching
        setFilters(localFilters);
        
        if (onSearch) {
            onSearch(searchValue, localFilters);
        }
    };

    const handleFilterChange = (key, value) => {
        const newFilters = { ...localFilters, [key]: value };
        setLocalFilters(newFilters);
        // Don't call onFilterChange immediately - only when search is clicked
    };

    const handleQuickFilter = (status) => {
        handleFilterChange('status', status);
    };

    return (
        <div className="auction-search-header" style={{ 
            marginBottom: 40,
            padding: '40px',
            backgroundImage: "url(/assets/images/hero-image.jpg)",
            backgroundSize: 'cover',
            backgroundPosition: 'center 100%',
            backgroundRepeat: 'no-repeat',
            // borderRadius: '20px',
            boxShadow: '0 20px 60px rgba(24, 144, 255, 0.2)',
            position: 'relative',
            overflow: 'hidden',
            margin: '0 0 40px 0',
            minHeight: '400px'
        }}>
            {/* Enhanced background decoration */}
            <div style={{
                position: 'absolute',
                top: -80,
                right: -80,
                width: 300,
                height: 300,
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '50%',
                zIndex: 1,
                animation: 'float 6s ease-in-out infinite'
            }} />
            <div style={{
                position: 'absolute',
                bottom: -50,
                left: -50,
                width: 200,
                height: 200,
                background: 'rgba(255, 255, 255, 0.08)',
                borderRadius: '50%',
                zIndex: 1,
                animation: 'float 8s ease-in-out infinite reverse'
            }} />
            <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 100,
                height: 100,
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '50%',
                zIndex: 1,
                animation: 'pulse 4s ease-in-out infinite'
            }} />

            <div style={{ position: 'relative', zIndex: 2 }}>
                <Row justify="space-between" align="middle" gutter={[24, 24]} className="search-header-row">
                    <Col>
                        <Title level={1} className="search-title" style={{ 
                            margin: 0, 
                            color: 'white',
                            fontWeight: 800,
                            fontSize: '42px',
                            lineHeight: 1.2,
                            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}>
                            {title}
                        </Title>
                        <Text className="search-subtitle" style={{ 
                            fontSize: '20px',
                            color: 'rgba(255, 255, 255, 0.95)',
                            fontWeight: 400,
                            display: 'block',
                            marginTop: '12px',
                            lineHeight: 1.5
                        }}>
                            {subtitle}
                        </Text>
                        {/* {showAuctionCount && (
                            <div style={{ marginTop: 24 }}>
                                <Tag 
                                    color="white" 
                                    style={{ 
                                        color: '#1890ff',
                                        fontWeight: 700,
                                        fontSize: '16px',
                                        padding: '8px 20px',
                                        borderRadius: '25px',
                                        border: 'none',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    {loading ? 'Loading auctions...' : `${auctionCount} Auctions Available`}
                                </Tag>
                            </div>
                        )} */}
                    </Col>
                </Row>

                <div className="search-content-container" style={{ 
                    marginTop: 40,
                    background: 'rgba(255, 255, 255, 0.98)',
                    borderRadius: '16px',
                    padding: '32px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    {/* Search Input Area */}
                    <Row gutter={[20, 20]} align="middle">
                        <Col xs={24}>
                            <div className="search-container">
                                <Input
                                    placeholder="Search auctions by title, description, or category..."
                                    value={localSearchInput}
                                    onChange={(e) => setLocalSearchInput(e.target.value)}
                                    onPressEnter={() => handleSearch(localSearchInput)}
                                    allowClear
                                    size="large"
                                    className="search-input"
                                />
                                <Button
                                    type="primary"
                                    icon={<SearchOutlined />}
                                    onClick={() => handleSearch(localSearchInput)}
                                    className="search-button"
                                    size="large"
                                >
                                    <span className="search-button-text">Search</span>
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Filter Section - Always Visible */}
                    <div className="filter-section-mobile" style={{ 
                        marginTop: 20,
                        padding: '16px 20px',
                        background: 'linear-gradient(135deg, #f6ffed 0%, #f0f9ff 100%)',
                        borderRadius: '12px',
                        border: '1px solid #d9f7be'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '12px', 
                            flexWrap: 'wrap' 
                        }}>
                            <Text className="filter-label-text" style={{ 
                                fontSize: '14px',
                                fontWeight: 600,
                                color: '#374151',
                                margin: 0,
                                whiteSpace: 'nowrap'
                            }}>
                                Filter
                            </Text>
                            <Space size="small" wrap className="filter-buttons-space">
                            <Button 
                                type={localFilters.status === 'all' ? 'primary' : 'default'}
                                size="small"
                                icon={<AppstoreOutlined />}
                                onClick={() => handleQuickFilter('all')}
                                className="filter-button-mobile"
                                style={{
                                    borderRadius: '20px',
                                    fontWeight: 500,
                                    border: localFilters.status === 'all' ? 'none' : '1px solid #e8eaed',
                                    height: '32px',
                                    padding: '0 16px',
                                    fontSize: '13px',
                                    boxShadow: localFilters.status === 'all' ? '0 2px 8px rgba(24, 144, 255, 0.2)' : '0 1px 4px rgba(0,0,0,0.04)'
                                }}
                            >
                                All Auctions
                            </Button>
                            <Button 
                                type={localFilters.status === 'active' ? 'primary' : 'default'}
                                size="small"
                                icon={<FireOutlined />}
                                onClick={() => handleQuickFilter('active')}
                                className="filter-button-mobile"
                                style={{
                                    borderRadius: '20px',
                                    fontWeight: 500,
                                    border: localFilters.status === 'active' ? 'none' : '1px solid #e8eaed',
                                    height: '32px',
                                    padding: '0 16px',
                                    fontSize: '13px',
                                    boxShadow: localFilters.status === 'active' ? '0 2px 8px rgba(24, 144, 255, 0.2)' : '0 1px 4px rgba(0,0,0,0.04)'
                                }}
                            >
                                Active
                            </Button>
                            <Button 
                                type={localFilters.status === 'upcoming' ? 'primary' : 'default'}
                                size="small"
                                icon={<ClockCircleOutlined />}
                                onClick={() => handleQuickFilter('upcoming')}
                                className="filter-button-mobile"
                                style={{
                                    borderRadius: '20px',
                                    fontWeight: 500,
                                    border: localFilters.status === 'upcoming' ? 'none' : '1px solid #e8eaed',
                                    height: '32px',
                                    padding: '0 16px',
                                    fontSize: '13px',
                                    boxShadow: localFilters.status === 'upcoming' ? '0 2px 8px rgba(24, 144, 255, 0.2)' : '0 1px 4px rgba(0,0,0,0.04)'
                                }}
                            >
                                Upcoming
                            </Button>
                            <Button 
                                type={localFilters.status === 'completed' ? 'primary' : 'default'}
                                size="small"
                                icon={<StarOutlined />}
                                onClick={() => handleQuickFilter('completed')}
                                className="filter-button-mobile"
                                style={{
                                    borderRadius: '20px',
                                    fontWeight: 500,
                                    border: localFilters.status === 'completed' ? 'none' : '1px solid #e8eaed',
                                    height: '32px',
                                    padding: '0 16px',
                                    fontSize: '13px',
                                    boxShadow: localFilters.status === 'completed' ? '0 2px 8px rgba(24, 144, 255, 0.2)' : '0 1px 4px rgba(0,0,0,0.04)'
                                }}
                            >
                                Completed
                            </Button>
                            </Space>
                        </div>
                    </div>
                    
                    {/* {showQuickFilters && (
                        <div style={{ 
                            marginTop: 28,
                            paddingTop: 28,
                            borderTop: '1px solid #e8eaed'
                        }}>
                            <Text style={{ 
                                fontSize: '16px',
                                color: '#374151',
                                fontWeight: 600,
                                marginBottom: '16px',
                                display: 'block'
                            }}>
                                Quick Filters:
                            </Text>
                            <Space wrap size="middle">
                                <Button 
                                    type={filters.status === 'active' ? 'primary' : 'default'}
                                    size="large"
                                    icon={<FireOutlined />}
                                    onClick={() => handleQuickFilter('active')}
                                    style={{
                                        borderRadius: '25px',
                                        fontWeight: 600,
                                        border: filters.status === 'active' ? 'none' : '2px solid #e8eaed',
                                        height: '44px',
                                        padding: '0 24px',
                                        fontSize: '14px',
                                        boxShadow: filters.status === 'active' ? '0 4px 12px rgba(24, 144, 255, 0.3)' : '0 2px 8px rgba(0,0,0,0.04)'
                                    }}
                                >
                                    Active Auctions
                                </Button>
                                <Button 
                                    type={filters.status === 'upcoming' ? 'primary' : 'default'}
                                    size="large"
                                    icon={<ClockCircleOutlined />}
                                    onClick={() => handleQuickFilter('upcoming')}
                                    style={{
                                        borderRadius: '25px',
                                        fontWeight: 600,
                                        border: filters.status === 'upcoming' ? 'none' : '2px solid #e8eaed',
                                        height: '44px',
                                        padding: '0 24px',
                                        fontSize: '14px',
                                        boxShadow: filters.status === 'upcoming' ? '0 4px 12px rgba(24, 144, 255, 0.3)' : '0 2px 8px rgba(0,0,0,0.04)'
                                    }}
                                >
                                    Upcoming
                                </Button>
                                <Button 
                                    type={filters.status === 'completed' ? 'primary' : 'default'}
                                    size="large"
                                    icon={<StarOutlined />}
                                    onClick={() => handleQuickFilter('completed')}
                                    style={{
                                        borderRadius: '25px',
                                        fontWeight: 600,
                                        border: filters.status === 'completed' ? 'none' : '2px solid #e8eaed',
                                        height: '44px',
                                        padding: '0 24px',
                                        fontSize: '14px',
                                        boxShadow: filters.status === 'completed' ? '0 4px 12px rgba(24, 144, 255, 0.3)' : '0 2px 8px rgba(0,0,0,0.04)'
                                    }}
                                >
                                    Completed
                                </Button>
                            </Space>
                        </div>
                    )} */}
                </div>
            </div>

            <style jsx>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-20px); }
                }
                @keyframes pulse {
                    0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) scale(1); }
                    50% { opacity: 0.8; transform: translate(-50%, -50%) scale(1.1); }
                
                @media (max-width: 768px) {
                    .auction-search-header {
                        padding: 20px 16px !important;
                        margin: 0 0 24px 0 !important;
                        min-height: 280px !important;
                    }
                    
                    .search-title {
                        font-size: 28px !important;
                        line-height: 1.3 !important;
                    }
                    
                    .search-subtitle {
                        font-size: 16px !important;
                        margin-top: 8px !important;
                    }
                    
                    .search-content-container {
                        margin-top: 24px !important;
                        padding: 20px 16px !important;
                        border-radius: 12px !important;
                    }
                    
                    .filter-section-mobile {
                        margin-top: 16px !important;
                        padding: 12px 16px !important;
                        border-radius: 10px !important;
                    }
                }
                
                @media (max-width: 480px) {
                    .auction-search-header {
                        padding: 16px 12px !important;
                        margin: 0 0 20px 0 !important;
                        min-height: 240px !important;
                    }
                    
                    .search-title {
                        font-size: 24px !important;
                        line-height: 1.3 !important;
                    }
                    
                    .search-subtitle {
                        font-size: 14px !important;
                        margin-top: 6px !important;
                    }
                    
                    .search-content-container {
                        margin-top: 20px !important;
                        padding: 16px 12px !important;
                        border-radius: 10px !important;
                    }
                    
                    .filter-section-mobile {
                        margin-top: 12px !important;
                        padding: 10px 12px !important;
                        border-radius: 8px !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default AuctionSearch; 